// src/store/api/orders.js

import api from './base';

/**
 * Get user's orders with pagination
 * @param {Object} params - Query parameters (page, limit, status)
 * @returns {Promise} - API response
 */
export const getUserOrders = async (params = {}) => {
  const response = await api.get('/orders', { params });
  return response.data;
};

/**
 * Get order details by orderId
 * @param {String} orderId - Order ID
 * @returns {Promise} - API response
 */
export const getOrderDetails = async (orderId) => {
  const response = await api.get(`/orders/${orderId}`);
  return response.data;
};

/**
 * Cancel an order
 * @param {String} orderId - Order ID
 * @param {Object} cancelData - Cancellation data (reason)
 * @returns {Promise} - API response
 */
export const cancelOrder = async (orderId, cancelData) => {
  const response = await api.post(`/orders/${orderId}/cancel`, cancelData);
  return response.data;
};

/**
 * Track order — returns order status + live shipment tracking data from Shiprocket
 * @param {String} orderId - Order ID
 * @returns {Promise} - API response with order + shipment timeline
 */
export const trackOrder = async (orderId) => {
  const response = await api.get(`/orders/${orderId}/track`);
  return response.data;
};

/**
 * Get order invoice as JSON (only for DELIVERED orders)
 * @param {String} orderId - Order ID
 * @returns {Promise} - API response with invoice data
 */
export const getOrderInvoice = async (orderId) => {
  const response = await api.get(`/orders/${orderId}/invoice`);
  return response.data;
};

/**
 * Download order invoice as PDF (only for DELIVERED orders)
 * Uses responseType blob so the browser can trigger a file download
 * @param {String} orderId - Order ID
 * @returns {Promise} - Blob response
 */
export const downloadInvoicePDF = async (orderId) => {
  const response = await api.get(`/orders/${orderId}/invoice/download`, {
    responseType: 'blob'
  });
  return response; // Return full response so caller can read headers + blob
};

/**
 * Request return for a delivered order
 * @param {String} orderId - Order ID
 * @param {Object} returnData - { reason, items[] }
 * @returns {Promise} - API response
 */
export const requestReturn = async (orderId, returnData) => {
  const response = await api.post(`/orders/${orderId}/return`, returnData);
  return response.data;
};

/**
 * Check if a pincode is serviceable before placing order
 * @param {String} pincode - 6-digit Indian PIN code
 * @returns {Promise} - API response with serviceability + expected delivery days
 */
export const checkPincodeServiceability = async (pincode) => {
  const response = await api.get(`/shipping/pincode/${pincode}`);
  return response.data;
};

/**
 * Get shipment details for an order (AWB, courier, live status)
 * @param {String} orderId - Order ID
 * @returns {Promise} - API response with shipment record
 */
export const getOrderShipments = async (orderId) => {
  const response = await api.get(`/shipping/orders/${orderId}/shipments`);
  return response.data;
};